import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { FormControl } from "@chakra-ui/react";
import CustomSelect from "~components/Form/components/Select";

const ReflexSelect = ({
  selectOptions,
  setSelectedVendedorId,
  trimestre = false,
  selectedVendedorId,
  setSelectedVendedorMes,
  mesAtual = false,
  vendedores,
  loading = false
}) => {
  const pathname = globalThis.location?.pathname || '';
  const initializedRef = useRef(false);

  const STORAGE_KEYS = useMemo(() => ({
    VENDEDOR: `reflex_selected_vendedor_${pathname.replace(/\//g, '_')}`,
    MES: `reflex_selected_mes_${pathname.replace(/\//g, '_')}`
  }), [pathname]);


  const gerarMesesDisponiveis = useCallback(() => {
    if (!vendedores || vendedores.length === 0) return [];


    const mesesSet = new Set();
    vendedores.forEach(vendedor => {
      if (vendedor.meses) {
        vendedor.meses.forEach(mesObj => {
          mesesSet.add(mesObj.mes);
        });
      }
    });


    const mesesArray = Array.from(mesesSet).map(mesValue => {

      const [mesAbr, anoShort] = mesValue.split('/');
      const ano = 2000 + parseInt(anoShort);

      const nomes = {
        "jan": "Janeiro", "fev": "Fevereiro", "mar": "Março",
        "abr": "Abril", "mai": "Maio", "jun": "Junho",
        "jul": "Julho", "ago": "Agosto", "set": "Setembro",
        "out": "Outubro", "nov": "Novembro", "dez": "Dezembro"
      };

      return {
        label: `${nomes[mesAbr]} de ${ano}`,
        value: mesValue
      };
    });


    const sorted = mesesArray.sort((a, b) => {

      const getMesIndex = (mes) => {
        const meses = ["jan", "fev", "mar", "abr", "mai", "jun",
                      "jul", "ago", "set", "out", "nov", "dez"];
        return meses.indexOf(mes.split('/')[0]);
      };

      const [mesA, anoA] = a.value.split('/');
      const [mesB, anoB] = b.value.split('/');

      if (anoA !== anoB) return parseInt(anoB) - parseInt(anoA);
      return getMesIndex(mesB) - getMesIndex(mesA);
    });

    if (pathname === "/reflex/historico") {
      const now = new Date();
      const mesAbrevs = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
      const mesAtualValue = `${mesAbrevs[now.getMonth()]}/${String(now.getFullYear()).slice(-2)}`;
      return sorted.filter(m => m.value !== mesAtualValue);
    }

    return sorted;
  }, [pathname, vendedores]);

  const meses = gerarMesesDisponiveis();


  const [selectedMes, setSelectedMes] = useState(null);


  const [vendedorSelectValue, setVendedorSelectValue] = useState(null);


  useEffect(() => {
    if (initializedRef.current || meses.length === 0) return;

    const savedVendedor = localStorage.getItem(STORAGE_KEYS.VENDEDOR);
    const savedMes = localStorage.getItem(STORAGE_KEYS.MES);

    let vendedorInicial = 0;
    let mesInicial = null;


    if (savedVendedor) {
      vendedorInicial = parseInt(savedVendedor, 10);
    }


    if (savedMes && !mesAtual) {
      try {
        const mesData = JSON.parse(savedMes);

        if (meses.some(m => m.value === mesData.value)) {
          mesInicial = mesData;
        }
      } catch (e) {
        console.error('Erro ao carregar mês salvo:', e);
      }
    }


    if (!mesInicial) {
      mesInicial = meses[0];
    }


    setSelectedVendedorId(vendedorInicial);

    if (selectOptions) {
      const initialOption = selectOptions.find(option => option.value === vendedorInicial);
      setVendedorSelectValue(initialOption || { label: "Visão Geral", value: 0 });
    }

    if (mesInicial) {
      setSelectedMes(mesInicial);
      if (mesAtual) {
        setSelectedVendedorMes(mesInicial);
      }
    }

    initializedRef.current = true;
  }, [STORAGE_KEYS.MES, STORAGE_KEYS.VENDEDOR, mesAtual, meses, selectOptions, setSelectedVendedorId, setSelectedVendedorMes]);


  useEffect(() => {
    if (selectedVendedorId !== undefined && selectedVendedorId !== null) {
      localStorage.setItem(STORAGE_KEYS.VENDEDOR, selectedVendedorId.toString());
    }
  }, [selectedVendedorId, STORAGE_KEYS.VENDEDOR]);


  useEffect(() => {
    if (selectedMes) {
      localStorage.setItem(STORAGE_KEYS.MES, JSON.stringify(selectedMes));
    }
  }, [selectedMes, STORAGE_KEYS.MES]);


  useEffect(() => {
    if (selectOptions) {
      const currentOption = selectOptions.find(option => option.value === selectedVendedorId);
      setVendedorSelectValue(currentOption || { label: "Visão Geral", value: 0 });
    }
  }, [selectedVendedorId, selectOptions]);


  useEffect(() => {
    if (mesAtual && pathname !== "/reflex/historico" && meses.length > 0) {
      const mesMaisRecente = meses[0];

      if (selectedMes?.value !== mesMaisRecente.value) {
        setSelectedMes(mesMaisRecente);
        if (mesAtual) {
          setSelectedVendedorMes(mesMaisRecente);
        }
      }
    }
  }, [mesAtual, meses, pathname, selectedMes?.value, setSelectedVendedorMes]);

const texto = useMemo(() => {
    const isHistorico = pathname === "/reflex/historico";
    const isTrimestre = Boolean(trimestre);
    const vendedorUnico = vendedores?.length === 1;
    const multiplosVendedores = vendedores?.length > 1;

    if (isHistorico) {
      if (multiplosVendedores) return <>Escolha o mês que deseja consultar:</>;
      if (vendedorUnico) return <>Histórico do vendedor</>;
    }

    if (isTrimestre) {
      if (multiplosVendedores) return <>Selecione o vendedor</>;
      if (vendedorUnico) return (
        <> Desempenho do <span className="text-secundaria">vendedor</span> no trimestre </>
      );
    }

    let mesExibido;
    if (!selectedMes) {
      // Se não há mês selecionado, usa o primeiro da lista (mais recente) para exibição
      if (meses.length > 0) {
        mesExibido = meses[0].label.replace(" de 20", "/");
      } else if (loading) {
        mesExibido = "Carregando...";
      } else {
        return <>Usuário sem meta Reflex cadastrada</>;
      }
    } else {
      mesExibido = selectedMes.label.replace(" de 20", "/");
    }

    return (
      <> Desempenho em <span className="text-secundaria">{mesExibido}</span> no Reflex: {} </>
    );
  }, [pathname, trimestre, vendedores, selectedMes, meses, loading]);

  const selectBaseProps = useMemo(
    () => ({
      className: "select-filter focus:outline-none focus:border-none focus:shadow-none focus-visible:outline-none focus-visible:border-none focus-visible:shadow-none",
      "data-testid": "local-select",
      label: "",
    }),
    []
  );




  return (
    <div className="flex flex-col md:flex-row justify-between items-center gap-x-4 gap-y-4">
      <h2 className="text-xl font-bold md:basis-2/4 text-center md:text-left">
        {texto}
      </h2>

      {selectOptions && vendedores.length > 1 && (
        <FormControl className="md:basis-2/4 h-[50px] w-full md:w-auto" data-testid="local-form-control">
          <CustomSelect
            {...selectBaseProps}
            name="visita"
            placeholder="Escolha o Vendedor:"
            spacingControl={true}
            value={vendedorSelectValue}
            options={selectOptions}
            onChange={(option) => {
              const value = option?.value || 0;
              setSelectedVendedorId(value);

              setVendedorSelectValue(option || { label: "Visão Geral", value: 0 });

              if (value === 0 && meses.length > 0) {
                const mesMaisRecente = meses[0];
                setSelectedMes(mesMaisRecente);
                if (mesAtual) {
                  setSelectedVendedorMes(mesMaisRecente);
                }
              }
            }}
          />
        </FormControl>
      )}
      {pathname === "/reflex/historico" && (
        <FormControl className="md:basis-2/4 h-[50px] w-full md:w-auto" data-testid="local-form-control">
          <CustomSelect
            {...selectBaseProps}
            name="mes"
            placeholder="Escolha o Mês:"
            options={meses}
            value={selectedMes}
            spacingControl={true}
            onChange={(value) => {
              setSelectedMes(value);
              if (mesAtual) {
                setSelectedVendedorMes(value);
              }
            }}
          />
        </FormControl>
      )}
    </div>
  );
};

ReflexSelect.propTypes = {
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  trimestre: PropTypes.bool,
  setSelectedVendedorId: PropTypes.func.isRequired,
  mesesOptions: PropTypes.array,
  selectedVendedorId: PropTypes.number,
  loading: PropTypes.bool,
};

export default ReflexSelect;